"use strict";
/**
 * Billing Configuration - Single Source of Truth
 *
 * Defines canonical pricing, credit allocation, and cost calculation
 * for the Aerotage billing system. Imported by frontend and backend.
 *
 * MAINTENANCE:
 * - Update BEDROCK_COSTS when Anthropic changes pricing or new model versions launch
 * - CUSTOMER_PRICES are explicitly defined (not derived via formula) for clean, marketable numbers
 * - Minimum 33% margin maintained across all models
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.AVERAGE_OUTPUT_RATIO = exports.CUSTOMER_PRICES = exports.MARGIN_MULTIPLIER = exports.BEDROCK_COSTS = exports.PLAN_TIERS = void 0;
exports.deriveModelFamily = deriveModelFamily;
exports.getCustomerTokenPrice = getCustomerTokenPrice;
exports.computeCustomerCostCents = computeCustomerCostCents;
exports.computeBedrockCostCents = computeBedrockCostCents;
exports.estimateRequestCostCents = estimateRequestCostCents;
exports.isBusinessEmail = isBusinessEmail;
// ─────────────────────────────────────────────────────────────
// Plan Tiers
// ─────────────────────────────────────────────────────────────
exports.PLAN_TIERS = {
    trial: {
        name: 'Trial',
        monthlyPricePerSeat: 0,
        includedCreditsTotal: 5000, // $50.00 total for 14-day trial (in cents)
        overageMultiplier: null, // no overage on trial -- hard cap
        maxSeats: 3,
        durationDays: 14,
        requiresBusinessEmail: true,
    },
    professional: {
        name: 'Professional',
        monthlyPricePerSeat: 29900, // $299.00 in cents
        includedCreditsPerSeatPerMonth: 7500, // $75.00 in cents
        overageMultiplier: 1.0,
        minSeats: 3,
        maxSeats: 50,
        billingCycle: 'annual',
    },
    enterprise: {
        name: 'Enterprise',
        monthlyPricePerSeat: null,
        includedCreditsPerSeatPerMonth: null,
        overageMultiplier: null,
        minSeats: null,
        maxSeats: null,
        billingCycle: 'annual',
    },
};
function deriveModelFamily(modelId) {
    if (modelId.includes('haiku'))
        return 'haiku';
    if (modelId.includes('opus'))
        return 'opus';
    return 'sonnet';
}
// ─────────────────────────────────────────────────────────────
// Bedrock Costs (what we pay AWS) — update when Anthropic changes pricing
// ─────────────────────────────────────────────────────────────
exports.BEDROCK_COSTS = {
    haiku: { inputPer1M: 1.00, outputPer1M: 5.00 },
    sonnet: { inputPer1M: 3.00, outputPer1M: 15.00 },
    opus: { inputPer1M: 15.00, outputPer1M: 75.00 },
};
// ─────────────────────────────────────────────────────────────
// Customer-facing token prices — explicitly defined for clean pricing
// Raw margin calc: Bedrock * 1.33 → rounded UP to nearest $0.25
// (Haiku out: $6.65→$6.75, Sonnet out: $19.95→$20, Opus out: $99.75→$100)
// ─────────────────────────────────────────────────────────────
exports.MARGIN_MULTIPLIER = 1.33;
exports.CUSTOMER_PRICES = {
    haiku: { inputPer1M: 1.50, outputPer1M: 6.75 },
    sonnet: { inputPer1M: 4.00, outputPer1M: 20.00 },
    opus: { inputPer1M: 20.00, outputPer1M: 100.00 },
};
// ─────────────────────────────────────────────────────────────
// Cost Calculation
// ─────────────────────────────────────────────────────────────
function getCustomerTokenPrice(modelFamily) {
    return exports.CUSTOMER_PRICES[modelFamily];
}
/**
 * Convert token usage to cost in cents using customer-facing prices.
 * All amounts are integer cents — no floating point currency math.
 */
function computeCustomerCostCents(modelFamily, inputTokens, outputTokens, overageMultiplier = 1.0) {
    const prices = exports.CUSTOMER_PRICES[modelFamily];
    const rawCostDollars = (inputTokens * prices.inputPer1M / 1000000) +
        (outputTokens * prices.outputPer1M / 1000000);
    return Math.ceil(rawCostDollars * overageMultiplier * 100);
}
/**
 * Convert token usage to cost in cents using Bedrock (our cost) prices.
 */
function computeBedrockCostCents(modelFamily, inputTokens, outputTokens) {
    const costs = exports.BEDROCK_COSTS[modelFamily];
    const rawCostDollars = (inputTokens * costs.inputPer1M / 1000000) +
        (outputTokens * costs.outputPer1M / 1000000);
    return Math.ceil(rawCostDollars * 100);
}
// ─────────────────────────────────────────────────────────────
// Pre-request Cost Estimation (for spend limit checks)
// ─────────────────────────────────────────────────────────────
exports.AVERAGE_OUTPUT_RATIO = {
    haiku: 2.0,
    sonnet: 1.5,
    opus: 1.0,
};
/**
 * Estimate cost before a request completes (input tokens known, output estimated).
 * Returns estimated customer cost in cents.
 */
function estimateRequestCostCents(modelFamily, inputTokens) {
    const estimatedOutputTokens = Math.ceil(inputTokens * exports.AVERAGE_OUTPUT_RATIO[modelFamily]);
    return computeCustomerCostCents(modelFamily, inputTokens, estimatedOutputTokens);
}
// ─────────────────────────────────────────────────────────────
// Business Email Validation
// ─────────────────────────────────────────────────────────────
const FREE_EMAIL_DOMAINS = new Set([
    'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
    'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com',
    'live.com', 'msn.com', 'me.com', 'mac.com',
]);
function isBusinessEmail(email) {
    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain)
        return false;
    return !FREE_EMAIL_DOMAINS.has(domain);
}
//# sourceMappingURL=billing-config.js.map